import { RendererV3 } from "./RendererV3";
import { createSystem, World } from "./SimV2";
import { CommonRuleLibrary } from "./CommonRuleLibrary";

// Define butcher state as a string union type instead of enum
type ButcherState = "idle" | "targeting" | "moving" | "butchering";

// Define tree chopping state as a string union type
type ChopState = "idle" | "targeting" | "moving" | "chopping";

// Define wool collection state as a string union type
type WoolCollectionState = "idle" | "targeting" | "moving" | "collecting";

// Define building types
export enum BuildingType {
  LumberMill = "lumberMill",
  Loom = "loom",
  Butchery = "butchery",
}

// Define a type for building costs
export interface BuildingCost {
  lumber: number;
  wool?: number;
  meat?: number;
}

// Define building costs
export const BuildingCosts: Record<BuildingType, BuildingCost> = {
  [BuildingType.LumberMill]: { lumber: 5 },
  [BuildingType.Loom]: { lumber: 3, wool: 2 },
  [BuildingType.Butchery]: { lumber: 3, meat: 1 },
};

export class VillageLevel {
  public world: World;
  // Track total resources for building
  public totalResources = {
    lumber: 0,
    wool: 0,
    meat: 0,
  };

  constructor() {
    this.world = new World(
      1000,
      {
        type: "string",
        xTarget: "int",
        yTarget: "int",
        lifetime: "int",
        // Add inventory components
        sheepMeat: "int",
        lumber: "int",
        wool: "int",
        // Add butchering components
        butcherState: "string",
        butcherTimer: "int",
        butcherTargetId: "int",
        // Add tree chopping components
        chopTreeRule: "boolean",
        chopState: "string",
        chopTimer: "int",
        chopTargetId: "int",
        // Add wool collection components
        collectWoolRule: "boolean",
        collectWoolState: "string",
        collectWoolTimer: "int",
        collectWoolTargetId: "int",
        // Add wanderer role
        wandererRule: "boolean",
        // Add building components
        buildingType: "string",
        productionTimer: "int",
        productionRate: "int",
        // Add animation components
        animationEmoji: "string", // For storing temporary animation emoji
        animationActive: "boolean", // Whether an animation is active
      },
      50,
      50
    );

    this.initLevel();
  }

  public initLevel(): void {
    let workerEmojis = ["👨", "👩", "👴", "👵"];
    let treeEmojis = ["🌲", "🌳", "🌴"];

    CommonRuleLibrary.registerMoveToTargetRule(this.world);
    CommonRuleLibrary.registerWanderRule(
      this.world,
      "wanderRule",
      "Wander around randomly",
      0.3
    );

    // Register tree reproduction rule
    this.registerTreeReproductionRule();

    // Register sheep reproduction rule
    this.registerSheepReproductionRule();

    // Register butcher sheep rule
    this.registerButcherSheepRule();

    // Register chop tree rule
    this.registerChopTreeRule();

    // Register wool collection rule
    this.registerCollectWoolRule();

    // Register resource collection rule
    this.registerResourceCollectionRule();

    // Add sheep
    for (let i = 0; i < 10; i++) {
      this.world.addEntity({
        type: "sheep",
        emoji: "🐑",
        x: i * 2 + 1,
        y: 2,
        xTarget: i * 3 + 2,
        yTarget: 4,
        wanderRule: 1,
        moveToTargetRule: 1,
        sheepReproductionRule: 1,
      });
    }

    // Add 10 trees in top 20x20 area
    for (let i = 0; i < 10; i++) {
      const x = Math.floor(Math.random() * 20);
      const y = Math.floor(Math.random() * 20);
      this.world.addEntity({
        type: "tree",
        emoji: treeEmojis[Math.floor(Math.random() * treeEmojis.length)],
        x: x,
        y: y,
        treeReproductionRule: 1,
      });
    }

    // Add 20 trees randomly across the remainder of the world
    for (let i = 0; i < 20; i++) {
      let x, y;
      // Ensure we're not placing in the top 40x40 area
      do {
        x = Math.floor(Math.random() * this.world.ecs.worldWidth);
        y = Math.floor(Math.random() * this.world.ecs.worldHeight);
      } while (x < 40 && y < 40);

      this.world.addEntity({
        type: "tree",
        emoji: treeEmojis[Math.floor(Math.random() * treeEmojis.length)],
        x: x,
        y: y,
        treeReproductionRule: 1,
      });
    }

    // Add workers with weighted role selection
    for (let i = 0; i < 10; i++) {
      // Define role weights
      const roleWeights = [
        { role: "butcher", weight: 5 },
        { role: "lumberjack", weight: 30 },
        { role: "wanderer", weight: 0 },
        { role: "woolCollector", weight: 20 },
      ];

      // Calculate total weight
      const totalWeight = roleWeights.reduce(
        (sum, role) => sum + role.weight,
        0
      );

      // Generate a random number between 0 and totalWeight
      const randomValue = Math.random() * totalWeight;

      // Select a role based on the random value
      let cumulativeWeight = 0;
      let selectedRole = "";

      for (const roleOption of roleWeights) {
        cumulativeWeight += roleOption.weight;
        if (randomValue <= cumulativeWeight) {
          selectedRole = roleOption.role;
          break;
        }
      }

      // Create worker with the selected role
      const workerEntity: any = {
        type: "worker",
        emoji: workerEmojis[i % workerEmojis.length],
        x: i * 2,
        y: 0,
        xTarget: i * 2 - 3,
        yTarget: 3,
        moveToTargetRule: 1,
        // Initialize all role-specific properties to default values
        butcherSheepRule: 0,
        butcherState: "idle",
        butcherTimer: 0,
        butcherTargetId: -1,
        sheepMeat: 0,
        chopTreeRule: 0,
        chopState: "idle",
        chopTimer: 0,
        chopTargetId: -1,
        lumber: 0,
        collectWoolRule: 0,
        collectWoolState: "idle",
        collectWoolTimer: 0,
        collectWoolTargetId: -1,
        wool: 0,
        wandererRule: 0,
      };

      // Set the specific role property
      switch (selectedRole) {
        case "butcher":
          workerEntity.butcherSheepRule = 1;
          break;
        case "lumberjack":
          workerEntity.chopTreeRule = 1;
          break;
        case "wanderer":
          workerEntity.wandererRule = 1;
          break;
        case "woolCollector":
          workerEntity.collectWoolRule = 1;
          break;
      }

      this.world.addEntity(workerEntity);
    }
  }

  // Method to build a new building
  public buildBuilding(
    buildingType: BuildingType,
    x: number,
    y: number
  ): boolean {
    // Check if we have enough resources
    const cost = BuildingCosts[buildingType];

    if (
      (cost.lumber && this.totalResources.lumber < cost.lumber) ||
      (cost.wool && this.totalResources.wool < cost.wool) ||
      (cost.meat && this.totalResources.meat < cost.meat)
    ) {
      console.log("Not enough resources to build", buildingType);
      return false;
    }

    // Check if the position is empty
    const entitiesAtPosition = this.world.ecs.getEntitiesAt(x, y);
    if (entitiesAtPosition.length > 0) {
      console.log("Cannot build on occupied tile");
      return false;
    }

    // Deduct resources
    if (cost.lumber) this.totalResources.lumber -= cost.lumber;
    if (cost.wool) this.totalResources.wool -= cost.wool;
    if (cost.meat) this.totalResources.meat -= cost.meat;

    // Create the building
    let emoji = "🏠"; // Default
    let productionRate = 0;

    switch (buildingType) {
      case BuildingType.LumberMill:
        emoji = "🏭";
        productionRate = 5; // Produces lumber faster
        break;
      case BuildingType.Loom:
        emoji = "🧵";
        productionRate = 3; // Produces wool items
        break;
      case BuildingType.Butchery:
        emoji = "🔪";
        productionRate = 2; // Processes meat faster
        break;
    }

    this.world.addEntity({
      type: "building",
      buildingType: buildingType,
      emoji: emoji,
      x: x,
      y: y,
      productionTimer: 0,
      productionRate: productionRate,
    });

    return true;
  }

  private registerResourceCollectionRule(): void {
    this.world.addRule(
      "resourceCollectionRule",
      "Collect resources from workers and add to total",
      (
        world: World,
        active: Array<boolean>,
        type: Array<string>,
        sheepMeat: Int32Array,
        lumber: Int32Array,
        wool: Int32Array
      ) => {
        // Reset totals
        this.totalResources.lumber = 0;
        this.totalResources.wool = 0;
        this.totalResources.meat = 0;

        // Sum up all resources from active entities
        for (let i = 0; i <= world.maxActiveId; i++) {
          if (active[i]) {
            if (lumber[i]) this.totalResources.lumber += lumber[i];
            if (wool[i]) this.totalResources.wool += wool[i];
            if (sheepMeat[i]) this.totalResources.meat += sheepMeat[i];
          }
        }
      }
    );
  }

  private registerTreeReproductionRule(): void {
    this.world.addRule(
      "treeReproductionRule",
      "Trees reproduce by creating new trees in adjacent empty spaces every 1000 ticks",
      (
        world: World,
        active: Array<boolean>,
        treeReproductionRule: Array<boolean>,
        x: Int32Array,
        y: Int32Array,
        emoji: Array<string>
      ) => {
        // Only process entities that have the treeReproductionRule component
        for (let i = 0; i <= world.maxActiveId; i++) {
          if (active[i] && treeReproductionRule[i]) {
            // Only reproduce every 1000 ticks % entity id
            if (world.ticks > 1000 && world.ticks % 1000 === i % 1000) {
              // Check adjacent spaces for an empty spot
              const directions = [
                { dx: -1, dy: 0 }, // Left
                { dx: 1, dy: 0 }, // Right
                { dx: 0, dy: -1 }, // Up
                { dx: 0, dy: 1 }, // Down
                { dx: -1, dy: -1 }, // Upper left
                { dx: 1, dy: -1 }, // Upper right
                { dx: -1, dy: 1 }, // Lower left
                { dx: 1, dy: 1 }, // Lower right
              ];

              // Shuffle directions for randomness
              for (let j = directions.length - 1; j > 0; j--) {
                const k = Math.floor(Math.random() * (j + 1));
                [directions[j], directions[k]] = [directions[k], directions[j]];
              }

              // Try each direction until we find an empty spot
              for (const dir of directions) {
                const newX = x[i] + dir.dx;
                const newY = y[i] + dir.dy;

                // Check if the position is within world bounds
                if (
                  newX >= 0 &&
                  newX < world.ecs.worldWidth &&
                  newY >= 0 &&
                  newY < world.ecs.worldHeight
                ) {
                  // Check if the position is empty
                  const entitiesAtPosition = world.ecs.getEntitiesAt(
                    newX,
                    newY
                  );
                  if (entitiesAtPosition.length === 0) {
                    // Create a new tree at this position
                    world.addEntity({
                      type: "tree",
                      emoji: emoji[i], // Use the same emoji as the parent tree
                      x: newX,
                      y: newY,
                      treeReproductionRule: 1,
                    });

                    // Only create one new tree per reproduction cycle
                    break;
                  }
                }
              }
            }
          }
        }
      }
    );
  }

  private registerSheepReproductionRule(): void {
    this.world.addRule(
      "sheepReproductionRule",
      "Sheep reproduce by creating new sheep in adjacent empty spaces every 250 ticks",
      (
        world: World,
        active: Array<boolean>,
        sheepReproductionRule: Array<boolean>,
        x: Int32Array,
        y: Int32Array,
        wanderRule: Array<boolean>,
        moveToTargetRule: Array<boolean>
      ) => {
        // Only process entities that have the sheepReproductionRule component
        for (let i = 0; i <= world.maxActiveId; i++) {
          if (active[i] && sheepReproductionRule[i]) {
            if (world.ticks > 2000 && world.ticks % 2000 === i % 2000) {
              // Check adjacent spaces for an empty spot
              const directions = [
                { dx: -1, dy: 0 }, // Left
                { dx: 1, dy: 0 }, // Right
                { dx: 0, dy: -1 }, // Up
                { dx: 0, dy: 1 }, // Down
                { dx: -1, dy: -1 }, // Upper left
                { dx: 1, dy: -1 }, // Upper right
                { dx: -1, dy: 1 }, // Lower left
                { dx: 1, dy: 1 }, // Lower right
              ];

              // Shuffle directions for randomness
              for (let j = directions.length - 1; j > 0; j--) {
                const k = Math.floor(Math.random() * (j + 1));
                [directions[j], directions[k]] = [directions[k], directions[j]];
              }

              // Try each direction until we find an empty spot
              for (const dir of directions) {
                const newX = x[i] + dir.dx;
                const newY = y[i] + dir.dy;

                // Check if the position is within world bounds
                if (
                  newX >= 0 &&
                  newX < world.ecs.worldWidth &&
                  newY >= 0 &&
                  newY < world.ecs.worldHeight
                ) {
                  // Check if the position is empty
                  const entitiesAtPosition = world.ecs.getEntitiesAt(
                    newX,
                    newY
                  );
                  if (entitiesAtPosition.length === 0) {
                    // Create a new sheep at this position
                    // Calculate new target positions for the new sheep
                    const newXTarget = Math.max(
                      0,
                      Math.min(
                        world.ecs.worldWidth - 1,
                        newX + Math.floor(Math.random() * 10) - 5
                      )
                    );
                    const newYTarget = Math.max(
                      0,
                      Math.min(
                        world.ecs.worldHeight - 1,
                        newY + Math.floor(Math.random() * 10) - 5
                      )
                    );

                    world.addEntity({
                      type: "sheep",
                      emoji: "🐑", // All sheep use the same emoji
                      x: newX,
                      y: newY,
                      xTarget: newXTarget,
                      yTarget: newYTarget,
                      wanderRule: wanderRule[i] ? 1 : 0,
                      moveToTargetRule: moveToTargetRule[i] ? 1 : 0,
                      sheepReproductionRule: 1,
                    });

                    // Only create one new sheep per reproduction cycle
                    break;
                  }
                }
              }
            }
          }
        }
      }
    );
  }

  private registerButcherSheepRule(): void {
    this.world.addRule(
      "butcherSheepRule",
      "Workers target nearby sheep, move to them, kill them, and collect meat",
      (
        world: World,
        active: Array<boolean>,
        butcherSheepRule: Array<boolean>,
        type: Array<string>,
        x: Int32Array,
        y: Int32Array,
        xTarget: Int32Array,
        yTarget: Int32Array,
        butcherState: Array<string>,
        butcherTimer: Int32Array,
        butcherTargetId: Int32Array,
        sheepMeat: Int32Array,
        emoji: Array<string>,
        animationEmoji: Array<string>,
        animationActive: Array<boolean>
      ) => {
        // Only process entities that have the butcherSheepRule component
        for (let i = 0; i <= world.maxActiveId; i++) {
          if (active[i] && butcherSheepRule[i] && type[i] === "worker") {
            // State machine for butchering process
            switch (butcherState[i]) {
              case "idle":
                // Reset any active animation
                if (animationActive[i]) {
                  animationActive[i] = false;
                  animationEmoji[i] = "";
                }

                // Find a nearby sheep to target
                const nearestSheep = this.findNearestSheep(world, x[i], y[i]);
                if (nearestSheep !== -1) {
                  // Target found, set target coordinates and change state
                  butcherTargetId[i] = nearestSheep;
                  xTarget[i] = x[nearestSheep];
                  yTarget[i] = y[nearestSheep];
                  butcherState[i] = "moving";
                }
                break;

              case "moving":
                // Check if we've reached the target sheep
                if (
                  butcherTargetId[i] !== -1 &&
                  active[butcherTargetId[i]] &&
                  Math.abs(Number(x[i]) - Number(x[butcherTargetId[i]])) <= 1 &&
                  Math.abs(Number(y[i]) - Number(y[butcherTargetId[i]])) <= 1
                ) {
                  // We're next to the sheep, start butchering
                  butcherState[i] = "butchering";
                  butcherTimer[i] = 30; // Set timer for 30 ticks

                  // Start animation
                  animationActive[i] = true;
                } else if (
                  butcherTargetId[i] === -1 ||
                  !active[butcherTargetId[i]]
                ) {
                  // Target is no longer valid, go back to idle
                  butcherState[i] = "idle";
                  butcherTargetId[i] = -1;
                }
                break;

              case "butchering":
                // Check if target is still valid
                if (butcherTargetId[i] === -1 || !active[butcherTargetId[i]]) {
                  // Target is no longer valid, go back to idle
                  butcherState[i] = "idle";
                  butcherTargetId[i] = -1;
                  animationActive[i] = false;
                  break;
                }

                // Decrement timer
                butcherTimer[i]--;

                // Update animation frame based on world ticks
                // Cycle through animation frames: worker emoji -> sheep emoji -> knife emoji
                const animationFrame = world.ticks % 3;

                // Update the worker's emoji based on the animation frame
                switch (animationFrame) {
                  case 0:
                    animationEmoji[i] = emoji[i]; // Original worker emoji
                    break;
                  case 1:
                    animationEmoji[i] = "🐑"; // Sheep emoji
                    break;
                  case 2:
                    animationEmoji[i] = "🔪"; // Knife emoji
                    break;
                }

                // Check if butchering is complete
                if (butcherTimer[i] <= 0) {
                  // Butchering complete, collect meat and deactivate sheep
                  sheepMeat[i]++;
                  active[butcherTargetId[i]] = false; // Kill the sheep

                  // Reset state and animation
                  butcherState[i] = "idle";
                  butcherTargetId[i] = -1;
                  animationActive[i] = false;
                  animationEmoji[i] = "";
                }
                break;
            }
          }
        }
      }
    );
  }

  private registerChopTreeRule(): void {
    this.world.addRule(
      "chopTreeRule",
      "Workers target nearby trees, move to them, chop them down, and collect lumber",
      (
        world: World,
        active: Array<boolean>,
        chopTreeRule: Array<boolean>,
        type: Array<string>,
        x: Int32Array,
        y: Int32Array,
        xTarget: Int32Array,
        yTarget: Int32Array,
        chopState: Array<string>,
        chopTimer: Int32Array,
        chopTargetId: Int32Array,
        lumber: Int32Array,
        emoji: Array<string>,
        animationEmoji: Array<string>,
        animationActive: Array<boolean>
      ) => {
        // Only process entities that have the chopTreeRule component
        for (let i = 0; i <= world.maxActiveId; i++) {
          if (active[i] && chopTreeRule[i] && type[i] === "worker") {
            // State machine for tree chopping process
            switch (chopState[i]) {
              case "idle":
                // Reset any active animation
                if (animationActive[i]) {
                  animationActive[i] = false;
                  animationEmoji[i] = "";
                }

                // Find a nearby tree to target
                const nearestTree = this.findNearestTree(world, x[i], y[i]);
                if (nearestTree !== -1) {
                  // Target found, set target coordinates and change state
                  chopTargetId[i] = nearestTree;
                  xTarget[i] = x[nearestTree];
                  yTarget[i] = y[nearestTree];
                  chopState[i] = "moving";
                }
                break;

              case "moving":
                // Check if we've reached the target tree
                if (
                  chopTargetId[i] !== -1 &&
                  active[chopTargetId[i]] &&
                  Math.abs(Number(x[i]) - Number(x[chopTargetId[i]])) <= 1 &&
                  Math.abs(Number(y[i]) - Number(y[chopTargetId[i]])) <= 1
                ) {
                  // We're next to the tree, start chopping
                  chopState[i] = "chopping";
                  chopTimer[i] = 50; // Set timer for 50 ticks (trees take longer to chop than butchering sheep)

                  // Start animation
                  animationActive[i] = true;
                } else if (chopTargetId[i] === -1 || !active[chopTargetId[i]]) {
                  // Target is no longer valid, go back to idle
                  chopState[i] = "idle";
                  chopTargetId[i] = -1;
                }
                break;

              case "chopping":
                // Check if target is still valid
                if (chopTargetId[i] === -1 || !active[chopTargetId[i]]) {
                  // Target is no longer valid, go back to idle
                  chopState[i] = "idle";
                  chopTargetId[i] = -1;
                  animationActive[i] = false;
                  break;
                }

                // Decrement timer
                chopTimer[i]--;

                // Update animation frame based on world ticks
                // Cycle through animation frames: worker emoji -> tree emoji -> axe emoji
                const animationFrame = world.ticks % 3;

                // Get the tree emoji from the target
                const treeEmoji = emoji[chopTargetId[i]];

                // Update the worker's emoji based on the animation frame
                switch (animationFrame) {
                  case 0:
                    animationEmoji[i] = emoji[i]; // Original worker emoji
                    break;
                  case 1:
                    animationEmoji[i] = treeEmoji; // Tree emoji (🌲, 🌳, or 🌴)
                    break;
                  case 2:
                    animationEmoji[i] = "🪓"; // Axe emoji
                    break;
                }

                // Check if chopping is complete
                if (chopTimer[i] <= 0) {
                  // Chopping complete, collect lumber and deactivate tree
                  lumber[i] += 3; // Trees provide more resources than sheep
                  active[chopTargetId[i]] = false; // Remove the tree

                  // Reset state and animation
                  chopState[i] = "idle";
                  chopTargetId[i] = -1;
                  animationActive[i] = false;
                  animationEmoji[i] = "";
                }
                break;
            }
          }
        }
      }
    );
  }

  private registerCollectWoolRule(): void {
    this.world.addRule(
      "collectWoolRule",
      "Workers target nearby sheep, move to them, collect wool, and leave the sheep alive",
      (
        world: World,
        active: Array<boolean>,
        collectWoolRule: Array<boolean>,
        type: Array<string>,
        x: Int32Array,
        y: Int32Array,
        xTarget: Int32Array,
        yTarget: Int32Array,
        collectWoolState: Array<string>,
        collectWoolTimer: Int32Array,
        collectWoolTargetId: Int32Array,
        wool: Int32Array,
        emoji: Array<string>,
        animationEmoji: Array<string>,
        animationActive: Array<boolean>
      ) => {
        // Only process entities that have the collectWoolRule component
        for (let i = 0; i <= world.maxActiveId; i++) {
          if (active[i] && collectWoolRule[i] && type[i] === "worker") {
            // State machine for wool collection process
            switch (collectWoolState[i]) {
              case "idle":
                // Reset any active animation
                if (animationActive[i]) {
                  animationActive[i] = false;
                  animationEmoji[i] = "";
                }

                // Find a nearby sheep to target
                const nearestSheep = this.findNearestSheep(world, x[i], y[i]);
                if (nearestSheep !== -1) {
                  // Target found, set target coordinates and change state
                  collectWoolTargetId[i] = nearestSheep;
                  xTarget[i] = x[nearestSheep];
                  yTarget[i] = y[nearestSheep];
                  collectWoolState[i] = "moving";
                }
                break;

              case "moving":
                // Check if we've reached the target sheep
                if (
                  collectWoolTargetId[i] !== -1 &&
                  active[collectWoolTargetId[i]] &&
                  Math.abs(Number(x[i]) - Number(x[collectWoolTargetId[i]])) <=
                    1 &&
                  Math.abs(Number(y[i]) - Number(y[collectWoolTargetId[i]])) <=
                    1
                ) {
                  // We're next to the sheep, start collecting wool
                  collectWoolState[i] = "collecting";
                  collectWoolTimer[i] = 20; // Set timer for 20 ticks (faster than butchering)

                  // Start animation
                  animationActive[i] = true;
                } else if (
                  collectWoolTargetId[i] === -1 ||
                  !active[collectWoolTargetId[i]]
                ) {
                  // Target is no longer valid, go back to idle
                  collectWoolState[i] = "idle";
                  collectWoolTargetId[i] = -1;
                }
                break;

              case "collecting":
                // Check if target is still valid
                if (
                  collectWoolTargetId[i] === -1 ||
                  !active[collectWoolTargetId[i]]
                ) {
                  // Target is no longer valid, go back to idle
                  collectWoolState[i] = "idle";
                  collectWoolTargetId[i] = -1;
                  animationActive[i] = false;
                  break;
                }

                // Decrement timer
                collectWoolTimer[i]--;

                // Update animation frame based on world ticks
                // Cycle through animation frames: worker emoji -> sheep emoji -> scissors emoji
                const animationFrame = world.ticks % 3;

                // Update the worker's emoji based on the animation frame
                switch (animationFrame) {
                  case 0:
                    animationEmoji[i] = emoji[i]; // Original worker emoji
                    break;
                  case 1:
                    animationEmoji[i] = "🐑"; // Sheep emoji
                    break;
                  case 2:
                    animationEmoji[i] = "✂️"; // Scissors emoji
                    break;
                }

                // Check if wool collection is complete
                if (collectWoolTimer[i] <= 0) {
                  // Wool collection complete, collect wool but keep sheep alive
                  wool[i] += 1;

                  // Reset state and animation
                  collectWoolState[i] = "idle";
                  collectWoolTargetId[i] = -1;
                  animationActive[i] = false;
                  animationEmoji[i] = "";
                }
                break;
            }
          }
        }
      }
    );
  }

  // Helper method to find the nearest sheep
  private findNearestSheep(
    world: World,
    workerX: number,
    workerY: number
  ): number {
    let nearestSheep = -1;
    let nearestDistance = Number.MAX_VALUE;

    const { active, type, x, y } = world.ecs.data;

    // Search through all entities
    for (let i = 0; i <= world.maxActiveId; i++) {
      if (active[i] && type[i] === "sheep") {
        // Convert Int32Array values to numbers for arithmetic operations
        const distance =
          Math.abs(Number(x[i]) - workerX) + Math.abs(Number(y[i]) - workerY);

        // Check if this sheep is closer than the current nearest
        if (distance < nearestDistance) {
          nearestDistance = distance;
          nearestSheep = i;
        }
      }
    }

    // Only target sheep within a reasonable distance (e.g., 20 tiles)
    return nearestDistance <= 20 ? nearestSheep : -1;
  }

  // Helper method to find the nearest tree
  private findNearestTree(
    world: World,
    workerX: number,
    workerY: number
  ): number {
    let nearestTree = -1;
    let nearestDistance = Number.MAX_VALUE;

    const { active, type, x, y } = world.ecs.data;

    // Search through all entities
    for (let i = 0; i <= world.maxActiveId; i++) {
      if (active[i] && type[i] === "tree") {
        // Convert Int32Array values to numbers for arithmetic operations
        const distance =
          Math.abs(Number(x[i]) - workerX) + Math.abs(Number(y[i]) - workerY);

        // Check if this tree is closer than the current nearest
        if (distance < nearestDistance) {
          nearestDistance = distance;
          nearestTree = i;
        }
      }
    }

    // Only target trees within a reasonable distance (e.g., 25 tiles)
    return nearestDistance <= 25 ? nearestTree : -1;
  }
}

export function init() {
  console.log("Initializing Village Level...");

  const canvasElement = document.querySelector("#simCanvas");
  if (!canvasElement) {
    console.error("Canvas element #simCanvas not found in the document!");
    console.log("Available elements:", document.querySelectorAll("canvas"));
    return;
  }

  console.log("Canvas element found:", canvasElement);

  const village = new VillageLevel();
  village.initLevel();

  const renderer = new RendererV3("#simCanvas", village.world);

  (window as any).renderer = renderer;
  (window as any).village = village;
}

document.addEventListener("DOMContentLoaded", init);
